import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Checkbox } from '#/shared/ui/checkbox'
import { CloseIcon } from '#/shared/ui/icons'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { Skeleton } from '#/shared/ui/skeleton'

const SKELETON_WIDTHS = ['w-32', 'w-24', 'w-40']

function SelectedTag({
  option,
  onRemove,
}: {
  option: MultiSelectOption
  onRemove: () => void
}) {
  const { t } = useTranslation()
  return (
    <span className="flex max-w-full shrink-0 animate-pop-in items-center gap-1 rounded-sm bg-blue-100 px-1.25 text-xs text-gray-900">
      {/* Labels can be a full sentence (a provider named after an SMS body),
          so the chip truncates rather than stretching the control. */}
      <span className="min-w-0 truncate" title={option.label}>
        {option.label}
      </span>
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onRemove}
        aria-label={t('multiSelect.removeItem', { label: option.label })}
        className="flex size-3 shrink-0 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-gray-900"
      >
        <CloseIcon className="block size-2" />
      </button>
    </span>
  )
}

function TokenInput({
  inputRef,
  selectedOptions,
  query,
  setQuery,
  placeholder,
  onRemove,
  onBackspace,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>
  selectedOptions: MultiSelectOption[]
  query: string
  setQuery: (value: string) => void
  placeholder?: string
  onRemove: (value: string) => void
  onBackspace: () => void
}) {
  const { t } = useTranslation()
  return (
    // Chips wrap, but the control is height-capped and scrolls: a long
    // selection must never push the option list or the Apply footer off-panel.
    <div
      onClick={() => inputRef.current?.focus()}
      className="flex max-h-20 min-h-8 flex-wrap items-center gap-1.25 overflow-y-auto rounded-md border border-gray-200 px-1.25 py-0.75 focus-within:focus-ring"
    >
      {selectedOptions.map((option) => (
        <SelectedTag
          key={option.value}
          option={option}
          onRemove={() => onRemove(option.value)}
        />
      ))}
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Backspace on an empty query pops the last chip — standard
          // token-input behaviour, and why the chips live inside the control.
          if (e.key === 'Backspace' && query === '') onBackspace()
        }}
        placeholder={
          selectedOptions.length === 0
            ? (placeholder ?? t('multiSelect.searchPlaceholder'))
            : undefined
        }
        className="input-mobile-safe min-w-20 flex-1 bg-transparent text-sm placeholder:text-gray-300 focus-visible:outline-none"
        autoFocus
      />
    </div>
  )
}

function OptionRows({
  options,
  selectedSet,
  atMax,
  loading,
  onToggle,
}: {
  options: MultiSelectOption[]
  selectedSet: Set<string>
  atMax: boolean
  loading?: boolean
  onToggle: (option: MultiSelectOption) => void
}) {
  const { t } = useTranslation()
  if (loading) {
    return (
      <>
        {SKELETON_WIDTHS.map((width) => (
          <li key={width} className="flex h-9 items-center px-2.5">
            <Skeleton className={cn('h-4', width)} />
          </li>
        ))}
      </>
    )
  }
  if (options.length === 0) {
    return (
      <li className="flex h-9 items-center px-2.5 text-sm text-gray-300">
        {t('multiSelect.noResults')}
      </li>
    )
  }
  return (
    <>
      {options.map((option) => {
        const isSelected = selectedSet.has(option.value)
        // Options past the cap stay visible but unselectable, so the list
        // doesn't silently shrink when the user hits `max`.
        const isDisabled = option.disabled ?? (atMax && !isSelected)
        return (
          <li key={option.value}>
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={isDisabled}
              onClick={() => onToggle(option)}
              className={cn(
                'flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                isDisabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                tabIndex={-1}
                aria-hidden
                className="pointer-events-none"
              />
              <span className="flex-1 truncate">{option.label}</span>
            </button>
          </li>
        )
      })}
    </>
  )
}

interface InlineOptionListProps {
  options: MultiSelectOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  loading?: boolean
  max?: number
}

/**
 * A searchable multi-select whose option list stays in flow instead of
 * floating above the panel.
 *
 * The filter sub-panel can't use `MultiSelect` here: its dropdown overlays the
 * panel and would cover the Cancel/Apply footer, leaving no way to apply the
 * filter. Same interaction — type to filter, chips for what's picked — but the
 * list pushes the footer down rather than hiding it.
 */
export function InlineOptionList({
  options,
  value,
  onValueChange,
  placeholder,
  loading,
  max,
}: InlineOptionListProps) {
  const [query, setQuery] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) =>
      option.label.toLowerCase().includes(needle),
    )
  }, [options, query])

  const selectedSet = React.useMemo(() => new Set(value), [value])
  const optionByValue = React.useMemo(() => {
    const map = new Map<string, MultiSelectOption>()
    for (const option of options) map.set(option.value, option)
    return map
  }, [options])

  // Chips follow selection order. A value whose option hasn't loaded yet (a
  // provider id, before the source list arrives) falls back to its raw value.
  const selectedOptions = value.map(
    (v) => optionByValue.get(v) ?? { value: v, label: v },
  )
  const atMax = max !== undefined && value.length >= max

  const remove = (target: string) =>
    onValueChange(value.filter((v) => v !== target))

  const toggle = (option: MultiSelectOption) => {
    if (selectedSet.has(option.value)) {
      remove(option.value)
      return
    }
    onValueChange([...value, option.value])
    setQuery('')
    inputRef.current?.focus()
  }

  return (
    <div className="flex flex-col gap-1.25">
      <TokenInput
        inputRef={inputRef}
        selectedOptions={selectedOptions}
        query={query}
        setQuery={setQuery}
        placeholder={placeholder}
        onRemove={remove}
        onBackspace={() => {
          if (value.length > 0) remove(value[value.length - 1])
        }}
      />
      <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
        <OptionRows
          options={filtered}
          selectedSet={selectedSet}
          atMax={atMax}
          loading={loading}
          onToggle={toggle}
        />
      </ul>
    </div>
  )
}
